import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form, Button, Row, Col, Card, ListGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useGetRentDetailsQuery,
  useUpdateRentMutation,
} from '../../slices/rentsApiSlice';
import { useGetHousesQuery } from '../../slices/housesApiSlice';
import { useGetTenantsQuery } from '../../slices/tenantsApiSlice';
import { useGetLandlordsQuery } from '../../slices/landlordsApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';

const RentEditScreen = () => {
  const { id: rentId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    houseInput: '',
    tenantInput: '',
    landlordInput: '',
    selectedHouse: null,
    selectedTenant: null,
    selectedLandlord: null,
    startDate: '',
    endDate: '',
    lastRentEnd: '',
    monthlyRent: '',
    numberOfMonths: '',
    date: '',
    datePaid: '',
    remark: '',
    images: [],
  });

  const [filteredHouses, setFilteredHouses] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [filteredLandlords, setFilteredLandlords] = useState([]);

  const { data: rent, isLoading, error, refetch } = useGetRentDetailsQuery(rentId);
  const [updateRent, { isLoading: saving }] = useUpdateRentMutation();
  const { data: housesData } = useGetHousesQuery({ keyword: formData.houseInput });
  const { data: tenantsData } = useGetTenantsQuery({ keyword: formData.tenantInput });
  const { data: landlordsData } = useGetLandlordsQuery({ keyword: formData.landlordInput });

  useEffect(() => {
    if (rent) {
      setFormData(prev => ({
        ...prev,
        selectedHouse: rent.house,
        houseInput: rent.house?.name || '',
        selectedTenant: rent.tenant,
        tenantInput: rent.tenant?.name || '',
        selectedLandlord: rent.landlord,
        landlordInput: rent.landlord?.name || '',
        startDate: rent.startDate?.substring(0, 10) || '',
        endDate: rent.endDate?.substring(0, 10) || '',
        lastRentEnd: rent.lastRentEnd?.substring(0, 10) || '',
        monthlyRent: rent.monthlyRent,
        numberOfMonths: rent.numberOfMonths,
        date: rent.date?.substring(0, 10) || '',
        datePaid: rent.datePaid?.substring(0, 10) || '',
        remark: rent.remark || '',
        images: rent.images || [],
      }));
    }
  }, [rent]);

  useEffect(() => {
    const filter = (input, list) =>
      input.trim()
        ? list.filter(item => item.name.toLowerCase().includes(input.toLowerCase())).slice(0, 5)
        : [];

    setFilteredHouses(filter(formData.houseInput, housesData?.houses || []));
    setFilteredTenants(filter(formData.tenantInput, tenantsData?.tenants || []));
    setFilteredLandlords(filter(formData.landlordInput, landlordsData?.landlords || []));
  }, [formData.houseInput, formData.tenantInput, formData.landlordInput, housesData, tenantsData, landlordsData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelect = (field, inputField, value) => {
    handleChange(field, value);
    handleChange(inputField, value.name);
    if (field === 'selectedHouse') setFilteredHouses([]);
    if (field === 'selectedTenant') setFilteredTenants([]);
    if (field === 'selectedLandlord') setFilteredLandlords([]);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    const {
      selectedHouse, selectedTenant, selectedLandlord,
      startDate, endDate, monthlyRent, numberOfMonths, date,
    } = formData;

    if (!selectedHouse || !selectedTenant || !selectedLandlord || !startDate || !endDate || !monthlyRent || !numberOfMonths || !date) {
      toast.error('Please fill all required fields');
      return;
    }

    const updatedData = {
      house: selectedHouse._id,
      tenant: selectedTenant._id,
      landlord: selectedLandlord._id,
      startDate: formData.startDate,
      endDate: formData.endDate,
      lastRentEnd: formData.lastRentEnd,
      monthlyRent: parseFloat(formData.monthlyRent),
      numberOfMonths: parseInt(formData.numberOfMonths),
      date: formData.date,
      datePaid: formData.datePaid,
      remark: formData.remark,
      images: formData.images,
    };

    try {
      await updateRent({ rentId, updatedData }).unwrap();
      toast.success('Rent record updated');
      refetch();
      navigate('/admin/rentlistscreen');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const renderAutocomplete = (label, inputKey, selectedKey, filteredList, onSelect) => (
    <Form.Group controlId={inputKey} className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="text"
        value={formData[inputKey]}
        onChange={(e) => handleChange(inputKey, e.target.value)}
        placeholder={`Type to search ${label.toLowerCase()}`}
        required
      />
      {filteredList.length > 0 && (
        <ListGroup className="dropdown-menu mt-2 show">
          {filteredList.map(item => (
            <ListGroup.Item key={item._id} action onClick={() => onSelect(item)}>
              {item.name}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {formData[selectedKey] && (
        <div className="mt-2"><strong>Selected:</strong> {formData[selectedKey].name}</div>
      )}
    </Form.Group>
  );


  return (
  <>
    <Link to="/admin/rentlistscreen" className="btn btn-light my-3">
      Go Back
    </Link>
    <Row>
      <Col md={8}>
        <h1>Edit Rent</h1>
        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">
            {error?.data?.message || 'Failed to load rent'}
          </Message>
        ) : (
          <Form onSubmit={submitHandler}>
            {renderAutocomplete(
              'House',
              'houseInput',
              'selectedHouse',
              filteredHouses,
              (h) => handleSelect('selectedHouse', 'houseInput', h)
            )}
            {renderAutocomplete(
              'Tenant',
              'tenantInput',
              'selectedTenant',
              filteredTenants,
              (t) => handleSelect('selectedTenant', 'tenantInput', t)
            )}
            {renderAutocomplete(
              'Landlord',
              'landlordInput',
              'selectedLandlord',
              filteredLandlords,
              (l) => handleSelect('selectedLandlord', 'landlordInput', l)
            )}

            {/* Independent date fields */}
            <Form.Group controlId="startDate" className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="endDate" className="mb-3">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="lastRentEnd" className="mb-3">
              <Form.Label>Last Rent End</Form.Label>
              <Form.Control
                type="date"
                value={formData.lastRentEnd}
                onChange={(e) => handleChange('lastRentEnd', e.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="date" className="mb-3">
              <Form.Label>Contract Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="datePaid" className="mb-3">
              <Form.Label>Date Paid</Form.Label>
              <Form.Control
                type="date"
                value={formData.datePaid}
                onChange={(e) => handleChange('datePaid', e.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="monthlyRent" className="mb-3">
              <Form.Label>Monthly Rent</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={formData.monthlyRent}
                onChange={(e) => handleChange('monthlyRent', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="numberOfMonths" className="mb-3">
              <Form.Label>Number of Months</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.numberOfMonths}
                onChange={(e) => handleChange('numberOfMonths', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="remark" className="mb-3">
              <Form.Label>Remark</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.remark}
                onChange={(e) => handleChange('remark', e.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="images" className="mb-3">
              <Form.Label>Upload Contract Images</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                onChange={(e) =>
                  handleChange('images', Array.from(e.target.files))
                }
              />
            </Form.Group>

            {formData.images.length > 0 && (
              <div className="mt-3">
                <strong>Preview:</strong>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {formData.images.map((file, index) => {
                    const src =
                      typeof file === 'string'
                        ? file
                        : URL.createObjectURL(file);
                    return (
                      <img
                        key={index}
                        src={src}
                        alt={`preview-${index}`}
                        style={{
                          width: '100px',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="mt-3"
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Update Rent'}
            </Button>
          </Form>
        )}
      </Col>

      <Col md={4}>
        <Card className="mt-4">
          <ListGroup variant="flush">
            <ListGroup.Item>
              <strong>Quick Tips:</strong>
              <ul className="mb-0">
                <li>Use autocomplete to select house, tenant, and landlord.</li>
                <li>Ensure all required fields are filled.</li>
                <li>Upload contract images if needed.</li>
              </ul>
            </ListGroup.Item>
            {saving && (
              <ListGroup.Item>
                <Loader />
              </ListGroup.Item>
            )}
          </ListGroup>
        </Card>
      </Col>
    </Row>
  </>
);
};

export default RentEditScreen;



              